// Wire protocol shared by the hub, house agents and controllers (dashboard /
// simulator). JSON over WebSocket. All times are absolute UTC epoch
// milliseconds; every machine keeps its clock right via NTP, so a show can say
// "start at 20:45:03.500" and each house schedules it locally.

import type { Program } from "./program.ts";

export type Rgb = [number, number, number];

export const LIMITS = {
  // Tempo range accepted for beat shows (real songs sit well inside it).
  minBpm: 20,
  maxBpm: 300,
  // Sections per strand in Govee real-time mode.
  maxSections: 15,
  maxPaletteColors: 8,
  maxOffsetMs: 2000,
  maxPosition: 999,
  maxDevices: 32,
  maxMessageBytes: 16 * 1024,
} as const;

// Effects that follow the beat. Whole-strand: step (new palette color each
// beat), chase (color travels house to house), pulse (soft swell), flash
// (snap bright, dark between), strobe (short sharp flashes). Per-section, for
// strands in real-time mode: run (a lit section runs along the strand), comet
// (a run with a longer glowing tail), marquee (several runs chasing evenly
// spaced, together), bounce (a lit section bounces back and forth instead of
// wrapping), crossrun (two lit sections run toward each other from opposite
// ends and cross), fill (color wipes along the strand), alternate (every
// other section swaps colors), twinkle (random sections sparkle), rainbow
// (colors flow along the strand). Strands that can't do sections get the
// closest whole-strand effect.
export const EFFECTS = [
  "step",
  "chase",
  "pulse",
  "flash",
  "strobe",
  "run",
  "comet",
  "marquee",
  "bounce",
  "crossrun",
  "fill",
  "alternate",
  "twinkle",
  "rainbow",
] as const;
export type EffectName = (typeof EFFECTS)[number];

export const SECTION_EFFECTS: readonly EffectName[] = [
  "run",
  "comet",
  "marquee",
  "bounce",
  "crossrun",
  "fill",
  "alternate",
  "twinkle",
  "rainbow",
];

// Cycles per beat: quarter (once a bar of 4), half, normal and double time.
export const RATES = [0.25, 0.5, 1, 2] as const;
export type Rate = (typeof RATES)[number];

interface BeatFields {
  bpm: number;
  palette: Rgb[];
  brightness: number;
  // Beat 0. Defaults to the activation time. Send the current show's value
  // to change palette or brightness without shifting the beat phase.
  startEpochMs?: number;
}

// What a controller asks for. The hub stamps it with an activation time.
export type ShowSpec =
  | { kind: "off" }
  | { kind: "solid"; rgb: Rgb; brightness: number }
  // beat:  every house changes color together on each beat.
  // chase: same, but each house is one palette step behind the previous
  //        street position, so colors travel down the street.
  // The original modes, identical to effects step and chase at rate 1; kept
  // so Pis that haven't updated yet keep working.
  | ({ kind: "beat" | "chase" } & BeatFields)
  | ({ kind: "effect"; effect: EffectName; rate: Rate } & BeatFields);

// Shows that follow a beat (everything but off and solid).
export type BeatShowSpec = Extract<ShowSpec, { bpm: number }>;

// What the hub broadcasts: the spec plus the time it takes effect.
export type Show = ShowSpec & { t: number };

export interface DeviceInfo {
  id: string;
  sku: string;
  ip?: string;
  lastSeen: number;
}

// Periodic agent -> hub report (also serves as the heartbeat).
export interface AgentReport {
  driver: string;
  devices: DeviceInfo[];
  // Agent clock minus hub clock, estimated from ping/pong. null until measured.
  clockSkewMs: number | null;
}

// Who runs a house's lights. Chosen by the household (consent): the street
// show, their own show, or nothing.
export type HouseMode = "street" | "own" | "off";

export interface HouseStyle {
  // Govee real-time mode: instant color changes (needs a supporting model).
  realtime: boolean;
  // Flash on each beat, change color while dim (for lights that fade).
  flash: boolean;
}

// Per-strand settings. A house can have several strands (each a Govee
// device), some able to do real-time mode and some not.
export interface StrandConfig {
  // Govee device id.
  id: string;
  // 0-based order within the house, for effects that travel along it.
  order: number;
  // Real-time mode for this strand (overrides the house's style.realtime).
  realtime: boolean;
  // Sections in real-time mode (default and maximum LIMITS.maxSections).
  segments?: number;
  // The strand runs the other way (its section 0 is at the far end).
  reverse?: boolean;
}

// Per-house settings the hub pushes to that house's agent.
export interface AgentConfig {
  offsetMs: number;
  position?: number;
  style?: HouseStyle;
  // Device ids the agent may control. Absent = all (development rooms).
  allowedDevices?: string[];
  // Absent = keep the current list ([] clears it). Strands not listed use
  // the house's style.realtime, discovery order, and LIMITS.maxSections
  // sections when real-time.
  strands?: StrandConfig[];
}

export interface HouseView extends AgentReport {
  house: string;
  position: number;
  agentVersion: string;
  connected: boolean;
  lastSeen: number;
  offsetMs: number;
  // Street (saved) houses only.
  address?: string;
  mode?: HouseMode;
  style?: HouseStyle;
  allowedDevices?: string[];
}

// The song the admin page is playing, for the public Listen page.
export interface NowPlaying {
  songId: string;
  title: string;
  // Wall-clock ms when the song's first sample is heard.
  startEpochMs: number;
  duration: number;
}

export type ClientMessage =
  // Development rooms: shared room key; the agent names its own house.
  | {
      type: "hello";
      role: "agent";
      room: string;
      key: string;
      house: string;
      position: number;
      agentVersion: string;
    }
  // Street: the house's own device key (from "Prepare a Pi").
  | { type: "hello"; role: "agent"; token: string; agentVersion: string }
  // Controllers: a room key, or (no key) the admin's signed-in browser session.
  | { type: "hello"; role: "controller"; room: string; key?: string }
  | { type: "status"; report: AgentReport }
  | { type: "ping"; clientTime: number }
  | { type: "set_show"; show: ShowSpec }
  | { type: "set_offset"; house: string; offsetMs: number }
  | { type: "now_playing"; song: NowPlaying | null };

// A song playing (or being tried) and the program this house should play to
// it. The Pi downloads the song's analysis once and renders the program from
// it, in step with the song, until the song ends.
export interface SongCue {
  songId: string;
  // Wall-clock ms when the song's first sample is heard.
  startEpochMs: number;
  duration: number;
  program: Program;
  // A household trying a program from its light board.
  preview?: boolean;
}

export type ServerMessage =
  | {
      type: "welcome";
      role: "agent" | "controller";
      serverTime: number;
      show: Show | null;
      // Agents only: this house's light offset.
      offsetMs?: number;
    }
  | { type: "show"; show: Show }
  // Agents only. Positive offset = this house's lights run ahead of the shared
  // timeline, to compensate for light or speaker latency at that house.
  | ({ type: "config" } & AgentConfig)
  | { type: "pong"; clientTime: number; serverTime: number }
  // Agents only: play this program with this song (null = no song program).
  | { type: "song"; cue: SongCue | null }
  // Controllers only.
  | { type: "houses"; houses: HouseView[] }
  | { type: "error"; message: string };

// WebSocket close codes the hub uses (4000-4999 is the application range).
export const CLOSE = {
  replaced: 4000, // another agent connected with the same house id
  helloTimeout: 4001,
  badMessage: 4002,
  unauthorized: 4003,
  rateLimited: 4008,
} as const;
